import { DndContext, type UniqueIdentifier } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  Calendar,
  Check,
  CircleDot,
  ClipboardCopy,
  Copy,
  Download,
  Dumbbell,
  EllipsisVertical,
  Eye,
  FileSpreadsheet,
  Folder,
  FolderOpen,
  FolderPlus,
  Pencil,
  Plus,
  Share2,
  Timer,
  Trash2,
  Upload,
} from "lucide-react"
import { DateTime } from "luxon"
import { useRef, useState } from "react"
import Draggable from "../components/draggable"
import ExerciseForm from "../components/ExerciseForm"
import { Button } from "../components/ui/button"
import { Calendar as CalendarComponent } from "../components/ui/calendar"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog"
import { Input } from "../components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Skeleton } from "../components/ui/skeleton"
import { Textarea } from "../components/ui/textarea"
import { useToast } from "../components/ui/use-toast"
import { fetchWorkoutsQueryOptions } from "../hooks/useWorkouts"
import { useWorkoutTemplateStore } from "../store"
import { apiFetch } from "../utils/apiFetch"
import { parseTemplateCsv } from "../utils/csvParsing"
import {
  displayDurationFrom,
  type TExercise,
  type TExerciseSet,
  type TFolder,
  type TWorkoutTemplate,
  templateColors,
} from "../utils/workoutUtils"

type TClonedWorkoutTemplate = {
  userId: string
  name: string
  status: "a" | "i"
  workoutTemplateId: string
  notes: string
  color: string
  jsonBlob: string
  dateCreated?: `${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`
}

export const Route = createFileRoute("/_app/templates")({
  component: WorkoutTemplates,
})

function getTemplateDates(date: string | null): string {
  if (!date) {
    return ""
  }

  const myDate = DateTime.fromISO(date)
  const now = DateTime.now()
  if (myDate.hasSame(now, "day", {})) {
    return `Today, ${myDate.toLocaleString(DateTime.DATE_FULL)}`
  }
  if (myDate.hasSame(now.minus({ days: 1 }), "day", {})) {
    return `Yesterday, ${myDate.toLocaleString(DateTime.DATE_FULL)}`
  }

  if (myDate.hasSame(now.plus({ days: 1 }), "day", {})) {
    return `Tomorrow, ${myDate.toLocaleString(DateTime.DATE_FULL)}`
  }

  return `${myDate.toLocaleString(DateTime.DATE_HUGE)}`
}

function buildTemplateCsv(template: TWorkoutTemplate): string {
  const header = "Template Name,Exercise,Set,Weight,Reps,RPE Range,Instructions"
  const rows: string[] = []

  const exercises = template.jsonBlob?.exercises || []
  exercises.forEach((exercise: TExercise) => {
    const rpeRange =
      exercise.rpeMin && exercise.rpeMax
        ? `${exercise.rpeMin}–${exercise.rpeMax}`
        : exercise.rpeMin
          ? String(exercise.rpeMin)
          : exercise.rpeMax
            ? String(exercise.rpeMax)
            : ""
    const instructions = (exercise.instructions || "").replace(/"/g, '""')
    ;(exercise.sets || []).forEach((set: TExerciseSet) => {
      rows.push(
        [template.name, exercise.name, set.order, set.weight, set.reps, rpeRange, instructions]
          .map((v) => `"${v}"`)
          .join(",")
      )
    })
  })

  return "\uFEFF" + header + "\n" + rows.join("\n")
}

function exportTemplateCsvDownload(template: TWorkoutTemplate) {
  const csv = buildTemplateCsv(template)
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("download", `${template.name.replace(/\s+/g, "_")}.csv`)
  link.click()
  URL.revokeObjectURL(url)
}

function exportTemplateCsvCopy(template: TWorkoutTemplate) {
  const csv = buildTemplateCsv(template)
  navigator.clipboard.writeText(csv)
}

function downloadSampleTemplateCsv() {
  const sample = `Template Name,Exercise,Set,Weight,Reps,RPE Range,Instructions
"Push Day A","Bench Press",1,185,8,8–9,"Barbell bench press, grip shoulder-width"
"Push Day A","Bench Press",2,205,6,8–9,
"Push Day A","Overhead Press",1,135,8,7–9,
"Push Day B","Squat",1,225,5,7–8,"Low bar squat"
"Push Day B","Squat",2,245,5,8–8,`
  const blob = new Blob(["\uFEFF" + sample], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("download", "OpenIron_Sample_Workout_Template.csv")
  link.click()
  URL.revokeObjectURL(url)
}

function WorkoutTemplates() {
  const queryClient = useQueryClient()
  const [showNewTemplateForm, setShowNewTemplateForm] = useState<boolean>(false)
  const [popoverTemplateId, setPopoverTemplateId] = useState<string>("")
  const { workoutTemplate, setWorkoutTemplate, exercises, setExercises, color, setColor } = useWorkoutTemplateStore(
    (state) => state
  )
  const { toast } = useToast()

  const [date, setDate] = useState<Date>(new Date())

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)

  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null)
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [useTemplateDialogOpen, setUseTemplateDialogOpen] = useState<boolean>(false)

  const [shareTemplateId, setShareTemplateId] = useState<string | null>(null)
  const [shareEmail, setShareEmail] = useState("")
  const [shareStatus, setShareStatus] = useState<"idle" | "sending" | "sent">("idle")

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [manageFolderOpen, setManageFolderOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState("")
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null)
  const [editingFolderName, setEditingFolderName] = useState("")

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importParsedData, setImportParsedData] = useState<Array<{
    name: string
    jsonBlob: { version: number; exercises: any[] }
  }> | null>(null)
  const [importResults, setImportResults] = useState<{ created: number; skipped: number } | null>(null)
  const [importFileName, setImportFileName] = useState("")

  const [showGoogleSheetsDialog, setShowGoogleSheetsDialog] = useState(false)
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState("")
  const [isImportingSheets, setIsImportingSheets] = useState(false)

  const { data: folders = [] } = useQuery({
    queryKey: ["folders"],
    queryFn: async () => {
      const data = await apiFetch<Array<TFolder>>("/v1/folders", { method: "GET" })
      return data
    },
  })

  const { data: workoutTemplates = [], isLoading: isLoadingWorkoutTemplates } = useQuery({
    queryKey: ["workoutTemplates"],
    queryFn: async () => {
      const data = await apiFetch<Array<TWorkoutTemplate>>("/v1/workoutTemplates", {
        method: "GET",
      })
      return data
    },
  })

  const { data: archivedWorkoutTemplates = [], isLoading: isLoadingArchivedWorkoutTemplates } = useQuery({
    queryKey: ["workoutTemplates-archived"],
    queryFn: async () => {
      const data = await apiFetch<Array<TWorkoutTemplate>>("/v1/workoutTemplates/archived", {
        method: "GET",
      })
      return data
    },
  })

  const { mutate: updateTemplate, isPending: isSavingTemplate } = useMutation({
    mutationFn: async (template: TWorkoutTemplate) => {
      await apiFetch(`/v1/workoutTemplates/${template.workoutTemplateId}`, {
        method: "PUT",
        body: JSON.stringify(template),
      })
    },
    onSuccess: () => {
      toast({
        title: `Template ${editingId ? "updated" : "created"}`,
        description: `Your "${workoutTemplate?.name}" template has been updated.`,
      })
      resetForm()
    },
    onError: (error) => {
      console.error(error)
      toast({
        title: "Error saving template",
        description: "Unknown error occurred while saving the template.",
        variant: "destructive",
      })
    },
  })

  const { mutate: cloneTemplate, isPending: isCloningTemplate } = useMutation({
    mutationFn: async (workoutTemplateId: string) => {
      const response = await apiFetch<TClonedWorkoutTemplate>(`/v1/workoutTemplates/clone/${workoutTemplateId}`, {
        method: "POST",
      })
      return response
    },
    onSuccess: (data: TClonedWorkoutTemplate) => {
      toast({
        title: `Template cloned`,
        description: `${data?.name} has been created.`,
      })
    },
    onError: (error) => {
      console.error(error)
      toast({
        title: "Error cloning template",
        description: "Unknown error occurred while cloning the template.",
        variant: "destructive",
      })
    },
  })

  const { mutate: archiveTemplate } = useMutation({
    mutationFn: async (templateId: string) => {
      await apiFetch(`/v1/workoutTemplates/${templateId}`, {
        method: "DELETE",
      })
    },
    onSuccess: () => {
      toast({
        title: `Template has been archived`,
        description: `Your template has been archived.`,
      })
      resetForm()
    },
    onError: (error) => {
      console.error(error)
      toast({
        title: "Error archiving template",
        description: "Unknown error occurred while archiving the template.",
        variant: "destructive",
      })
    },
  })

  const { mutateAsync: scheduleTemplate, isPaused: isSchedulingTemplate } = useMutation({
    mutationFn: async (data: { templateId: string; dateScheduled: string | null }) => {
      if (!data.dateScheduled) {
        throw new Error("Date scheduled is required")
      }
      await apiFetch("/v1/workouts/workoutTemplates/planned", {
        method: "POST",
        body: JSON.stringify({
          workoutTemplateId: data.templateId,
          dateScheduled: data.dateScheduled,
        }),
      })
    },
    onSuccess: () => {
      setSelectedTemplateId("")
      setPopoverTemplateId("")
      setUseTemplateDialogOpen(false)
      queryClient.refetchQueries({
        queryKey: fetchWorkoutsQueryOptions.queryKey,
      })
      toast({
        title: "Template Scheduled",
        description: `Your template has been added to your workout plan for ${date.toLocaleDateString()}.`,
      })
    },
    onError: (err) => {
      if (typeof err === "string") {
        toast({
          title: "Could not schedule your Template",
          description: err,
        })
      } else {
        toast({
          title: "Could not schedule your Template",
          description: `Your template could not be added, please try again later`,
        })
      }
    },
  })

  const { mutateAsync: shareTemplate } = useMutation({
    mutationFn: async (data: { workoutTemplateId: string; recipientEmail: string }) =>
      apiFetch("/v1/templateShares", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => {
      setShareStatus("sent")
      toast({ title: "Invite sent!" })
    },
    onError: () => toast({ title: "Could not share", variant: "destructive" }),
  })

  const { mutateAsync: importTemplates, isPending: isImporting } = useMutation({
    mutationFn: async ({
      templates,
      existingNames,
    }: {
      templates: Array<{ name: string; jsonBlob: { version: number; exercises: any[] } }>
      existingNames: string[]
    }) => {
      const toCreate = templates.filter((t) => !existingNames.includes(t.name))
      const skipped = templates.length - toCreate.length

      const results = await Promise.allSettled(
        toCreate.map((t) =>
          apiFetch("/v1/workoutTemplates", {
            method: "POST",
            body: JSON.stringify({
              name: t.name,
              jsonBlob: t.jsonBlob,
              notes: "",
              color: "#3b82f6",
              folderId: null,
            }),
          })
        )
      )

      const created = results.filter((r) => r.status === "fulfilled").length
      return { created, skipped }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["workoutTemplates"] })
      setImportResults(data)
    },
    onError: () => {
      toast({
        title: "Import failed",
        description: "Could not import templates. Please try again.",
        variant: "destructive",
      })
    },
  })

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const csvText = event.target?.result as string
        const parsed = parseTemplateCsv(csvText)

        if (parsed.length === 0) {
          toast({
            title: "No templates found",
            description: "CSV file contains no valid template data.",
            variant: "destructive",
          })
          return
        }

        setImportParsedData(parsed)
        setImportFileName(file.name)
        setImportResults(null)
        setImportDialogOpen(true)
      } catch (_err) {
        toast({
          title: "Failed to parse CSV",
          description: "Check the file format and try again.",
          variant: "destructive",
        })
      }
    }
    reader.readAsText(file)
    e.target.value = ""
  }

  const handleImportFromGoogleSheets = async () => {
    if (!googleSheetsUrl.trim()) {
      toast({
        title: "URL required",
        description: "Please enter a Google Sheets URL.",
        variant: "destructive",
      })
      return
    }

    setIsImportingSheets(true)
    try {
      const url = googleSheetsUrl.replace(/\/edit.*$/, "/export?format=csv")
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error("Failed to fetch CSV")
      }
      const csvText = await response.text()
      const parsed = parseTemplateCsv(csvText)

      if (parsed.length === 0) {
        toast({
          title: "No templates found",
          description: "The sheet contains no valid template data.",
          variant: "destructive",
        })
        setIsImportingSheets(false)
        return
      }

      setImportParsedData(parsed)
      setImportFileName(googleSheetsUrl)
      setImportResults(null)
      setShowGoogleSheetsDialog(false)
      setGoogleSheetsUrl("")
      setImportDialogOpen(true)
    } catch (_err) {
      toast({
        title: "Failed to import from Google Sheets",
        description: "Check the URL and make sure the sheet is publicly accessible.",
        variant: "destructive",
      })
    }
    setIsImportingSheets(false)
  }

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false)
    setImportParsedData(null)
    setImportResults(null)
    setImportFileName("")
  }

  const { mutate: createFolder } = useMutation({
    mutationFn: async (name: string) => {
      await apiFetch("/v1/folders", { method: "POST", body: JSON.stringify({ name }) })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["folders"] })
      setNewFolderName("")
      toast({ title: "Folder created" })
    },
    onError: () => toast({ title: "Could not create folder", variant: "destructive" }),
  })

  const { mutate: renameFolder } = useMutation({
    mutationFn: async ({ folderId, name }: { folderId: string; name: string }) => {
      await apiFetch(`/v1/folders/${folderId}`, { method: "PUT", body: JSON.stringify({ name }) })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["folders"] })
      queryClient.invalidateQueries({ queryKey: ["workoutTemplates"] })
      setEditingFolderId(null)
      setEditingFolderName("")
      toast({ title: "Folder renamed" })
    },
    onError: () => toast({ title: "Could not rename folder", variant: "destructive" }),
  })

  const { mutate: deleteFolder } = useMutation({
    mutationFn: async (folderId: string) => {
      await apiFetch(`/v1/folders/${folderId}`, { method: "DELETE" })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["folders"] })
      queryClient.invalidateQueries({ queryKey: ["workoutTemplates"] })
      if (selectedFolderId) {
        setSelectedFolderId(null)
      }
      toast({ title: "Folder deleted" })
    },
    onError: () => toast({ title: "Could not delete folder", variant: "destructive" }),
  })

  const { mutate: assignTemplateFolder } = useMutation({
    mutationFn: async ({ workoutTemplateId, folderId }: { workoutTemplateId: string; folderId: string | null }) => {
      await apiFetch(`/v1/folders/templates/${workoutTemplateId}`, {
        method: "PUT",
        body: JSON.stringify({ folderId }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workoutTemplates"] })
      toast({ title: "Template moved" })
    },
    onError: () => toast({ title: "Could not move template", variant: "destructive" }),
  })

  const handleAddExercise = () => {
    const newExercise: TExercise = {
      exerciseId: crypto.randomUUID(),
      name: "",
      expanded: true,
      sets: [
        {
          setId: crypto.randomUUID(),
          weight: 0,
          reps: 0,
          isCompleted: false,
          order: 1,
        },
      ],
    }

    setExercises([...exercises, newExercise])
  }

  const handleExerciseUpdate = (updatedExercise: TExercise) => {
    setExercises(
      exercises.map((exercise) => (exercise.exerciseId === updatedExercise.exerciseId ? updatedExercise : exercise))
    )
  }

  const handleExerciseDelete = (exerciseId: string) => {
    setExercises(exercises.filter((exercise) => exercise.exerciseId !== exerciseId))
  }

  const resetForm = () => {
    setDate(new Date())
    setWorkoutTemplate(null)
    setExercises([])
    setEditingId(null)
    setColor("gray")
    setShowNewTemplateForm(false)
  }

  const handleSubmit = () => {
    if (workoutTemplate?.name?.trim() === "") {
      toast({
        title: "Name required",
        description: "Please enter a name for your template.",
        variant: "destructive",
      })
      return
    }

    if (exercises.length === 0) {
      toast({
        title: "Exercises required",
        description: `Please add at least one exercise to your template`,
        variant: "destructive",
      })
      return
    }

    // Check for empty exercise names
    const hasEmptyExerciseName = exercises.some((exercise) => exercise.name.trim() === "")
    if (hasEmptyExerciseName) {
      toast({
        title: "Exercise names required",
        description: "Please provide a name for all exercises.",
        variant: "destructive",
      })
      return
    }

    const templateData: TWorkoutTemplate = {
      workoutTemplateId: editingId || crypto.randomUUID(),
      name: workoutTemplate?.name.trim() ?? "",
      jsonBlob: { version: 1, exercises: exercises },
      notes: workoutTemplate?.notes?.trim() || "",
      color: color,
      folderId: workoutTemplate?.folderId || null,
    }

    updateTemplate(templateData)
  }

  const [_, setIsDropped] = useState(false)
  const [draggingStatus, setDraggingStatus] = useState<"idle" | "dragging">("idle")
  function handleDragEnd(event) {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = exercises.findIndex((i) => i.exerciseId === active.id)
      const newIndex = exercises.findIndex((i) => i.exerciseId === over.id)
      const newArray = [...exercises]
      const [movedItem] = newArray.splice(oldIndex, 1)
      newArray.splice(newIndex, 0, movedItem)
      setExercises(newArray)
    }

    if (event.over) {
      setIsDropped(true)
    }
  }

  function handleDragStart() {
    setDraggingStatus("dragging")
  }

  function handleDragCancel() {}

  const visibleTemplates = selectedFolderId
    ? workoutTemplates.filter((t) => t.folderId === selectedFolderId)
    : workoutTemplates

  const groupedTemplates = selectedFolderId
    ? null
    : workoutTemplates.reduce<Record<string, TWorkoutTemplate[]>>((acc, t) => {
        const key = t.folderId || "__none__"
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(t)
        return acc
      }, {})

  const renderTemplateItem = (template: TWorkoutTemplate) => (
    <div
      key={template.workoutTemplateId}
      className="flex flex-col gap-2 border-border border-b py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {template.color && (
          <CircleDot
            className={`h-4 w-4 shrink-0 text-blue-300 ${template.color.replace("bg-", "text-").replace("-300", "-500")}`}
            style={{ color: template.color }}
          />
        )}
        <div className="min-w-0">
          <h3 className="font-medium">{template.name}</h3>
          <div className="flex gap-4 text-muted-foreground text-sm">
            <span className="flex items-center">
              <Dumbbell size={14} className="mr-1" />
              {template.jsonBlob.exercises.length} exercises
            </span>
            {template.averageDuration && (
              <span className="flex items-center">
                <Timer size={14} className="mr-1" />
                Average Duration: {displayDurationFrom(template.averageDuration)}
              </span>
            )}
          </div>
          {template.dateScheduled && (
            <div className="text-muted-foreground text-sm">
              Next scheduled: {getTemplateDates(template.dateScheduled)}
            </div>
          )}
          {template.lastDateCompleted && (
            <div className="text-muted-foreground text-sm">
              Last Completed: {getTemplateDates(template.lastDateCompleted)}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 sm:gap-2">
        <Select
          value={template.folderId || "__none__"}
          onValueChange={(value) =>
            assignTemplateFolder({
              workoutTemplateId: template.workoutTemplateId,
              folderId: value === "__none__" ? null : value,
            })
          }
        >
          <SelectTrigger className="h-8 w-32 sm:w-40">
            <SelectValue placeholder="No folder" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No folder</SelectItem>
            {folders.map((folder) => (
              <SelectItem key={folder.folderId} value={folder.folderId}>
                {folder.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Popover
          open={popoverTemplateId === template.workoutTemplateId}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setPopoverTemplateId("")
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (popoverTemplateId === template.workoutTemplateId) {
                  setPopoverTemplateId("")
                  return
                }
                setPopoverTemplateId(template.workoutTemplateId)
              }}
            >
              <EllipsisVertical size={16} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex flex-col gap-2">
            <Dialog
              open={archiveConfirmId === template.workoutTemplateId}
              onOpenChange={(open) => {
                if (!open) {
                  setArchiveConfirmId(null)
                }
              }}
            >
              <DialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  onClick={() => setArchiveConfirmId(template.workoutTemplateId)}
                >
                  <Trash2 size={16} />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Archive Template</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to archive the "{template.name}" template? This action cannot be undone (for
                    now).
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="flex flex-wrap gap-3">
                  <Button type="button" variant="outline" onClick={() => setArchiveConfirmId(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      archiveTemplate(template.workoutTemplateId)
                      setPopoverTemplateId("")
                    }}
                  >
                    Archive
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog
              open={viewingId === template.workoutTemplateId}
              onOpenChange={(open) => {
                if (!open) {
                  setViewingId(null)
                }
              }}
            >
              <DialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setViewingId(template.workoutTemplateId)
                  }}
                >
                  <Eye size={16} />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{template.name}</DialogTitle>
                  <div className="mt-5 flex max-h-[75vh] flex-col gap-3 overflow-y-auto">
                    {template.jsonBlob.exercises.map((exercise: TExercise, exerciseIndex: number) => (
                      <div key={exercise.exerciseId ?? exerciseIndex} className="rounded-md border">
                        <div className="flex items-center justify-between p-3">
                          <div>
                            <h4 className="font-medium">{exercise.name}</h4>
                            <div className="text-muted-foreground text-sm">
                              {exercise.sets.length} sets ·{"  "}
                              {exercise.sets
                                .reduce((total, set) => {
                                  return total + set.weight * set.reps
                                }, 0)
                                .toLocaleString()}{" "}
                              lbs total
                              {exercise.rpeMin || exercise.rpeMax
                                ? ` · RPE ${exercise.rpeMin ?? "?"}–${exercise.rpeMax ?? "?"}`
                                : ""}
                            </div>
                          </div>
                        </div>
                        <div className="px-3 pb-3">
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            {exercise.sets.map((set: TExerciseSet, index: number) => (
                              <div
                                key={set.setId ?? `${exerciseIndex}-${index}`}
                                className="rounded-md bg-muted/30 p-2"
                              >
                                <div className="font-medium">Set {index + 1}</div>
                                <div className="text-muted-foreground">
                                  {set.weight} lbs × {set.reps} reps
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </DialogHeader>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setViewingId(null)}>
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setEditingId(template.workoutTemplateId)
                setWorkoutTemplate({
                  name: template.name,
                  workoutTemplateId: template.workoutTemplateId,
                  notes: template.notes || "",
                  jsonBlob: template.jsonBlob,
                  color: template.color || "gray",
                  folderId: template.folderId || null,
                })
                setColor(template.color || "gray")
                setExercises(
                  template.jsonBlob.exercises.map((exercise: TExercise) => {
                    return {
                      ...exercise,
                      exerciseId: crypto.randomUUID(),
                      sets: exercise.sets.map((set) => {
                        return {
                          ...set,
                          setId: crypto.randomUUID(),
                        }
                      }),
                      expanded: true,
                    }
                  })
                )
                setPopoverTemplateId("")

                document.getElementById("template-header")?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                  inline: "nearest",
                })
              }}
            >
              <Pencil size={16} />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                cloneTemplate(template.workoutTemplateId)
                setPopoverTemplateId("")
              }}
              disabled={isCloningTemplate}
            >
              <Copy size={16} />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                exportTemplateCsvDownload(template)
                setPopoverTemplateId("")
                toast({ title: "CSV exported", description: `"${template.name}" has been downloaded.` })
              }}
            >
              <Download size={16} />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                exportTemplateCsvCopy(template)
                setPopoverTemplateId("")
                toast({ title: "CSV copied", description: `"${template.name}" CSV copied to clipboard.` })
              }}
            >
              <ClipboardCopy size={16} />
            </Button>
            <Dialog
              open={shareTemplateId === template.workoutTemplateId}
              onOpenChange={(open) => {
                if (!open) {
                  setShareTemplateId(null)
                  setShareEmail("")
                  setShareStatus("idle")
                }
              }}
            >
              <DialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShareTemplateId(template.workoutTemplateId)
                    setShareStatus("idle")
                    setShareEmail("")
                  }}
                >
                  <Share2 size={16} />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Share Template</DialogTitle>
                  <DialogDescription>Send an invite to share "{template.name}" with someone.</DialogDescription>
                </DialogHeader>
                {shareStatus === "sent" ? (
                  <p className="text-muted-foreground text-sm">Invite sent to {shareEmail}!</p>
                ) : (
                  <div className="space-y-3 py-2">
                    <Input
                      type="email"
                      placeholder="friend@example.com"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                    />
                  </div>
                )}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShareTemplateId(null)
                      setShareEmail("")
                      setShareStatus("idle")
                    }}
                  >
                    {shareStatus === "sent" ? "Close" : "Cancel"}
                  </Button>
                  {shareStatus !== "sent" && (
                    <Button
                      type="button"
                      disabled={shareStatus === "sending" || !shareEmail}
                      onClick={async () => {
                        setShareStatus("sending")
                        await shareTemplate({
                          workoutTemplateId: template.workoutTemplateId,
                          recipientEmail: shareEmail,
                        })
                      }}
                    >
                      {shareStatus === "sending" ? "Sending..." : "Send Invite"}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog
              open={useTemplateDialogOpen && selectedTemplateId === template.workoutTemplateId}
              onOpenChange={(open) => {
                if (!open) {
                  setUseTemplateDialogOpen(false)
                  setSelectedTemplateId(null)
                }
              }}
            >
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedTemplateId(template.workoutTemplateId)
                    setUseTemplateDialogOpen(true)
                  }}
                >
                  <Calendar size={16} />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Plan a Workout</DialogTitle>
                  <DialogDescription>Select a date for your "{template.name}" workout.</DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <CalendarComponent
                    mode="single"
                    selected={date}
                    onSelect={(date) => date && setDate(date)}
                    className="mx-auto"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setUseTemplateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={async () => {
                      await scheduleTemplate({
                        templateId: template.workoutTemplateId,
                        dateScheduled: !date ? DateTime.now().toISODate() : DateTime.fromJSDate(date).toISODate(),
                      })
                    }}
                    disabled={isSchedulingTemplate}
                  >
                    {isSchedulingTemplate ? <div>Scheduling...</div> : "Add to Plan"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <Card className="workout-card">
        <CardHeader>
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Templates</CardTitle>
            <div className="flex flex-wrap gap-2">
              <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImportFile} className="hidden" />
              <Button variant="outline" size="sm" onClick={downloadSampleTemplateCsv}>
                <Download size={14} className="mr-1" />
                Sample CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload size={14} className="mr-1" />
                Import CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowGoogleSheetsDialog(true)}>
                <FileSpreadsheet size={14} className="mr-1" />
                Sheets
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setShowNewTemplateForm(!showNewTemplateForm)
                  setWorkoutTemplate({} as TWorkoutTemplate)
                }}
              >
                <Plus size={14} className="mr-1" />
                New
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-1">
            <Button
              variant={selectedFolderId === null ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedFolderId(null)}
            >
              <Folder size={14} className="mr-1" />
              All
            </Button>
            {folders.map((folder) => (
              <Button
                key={folder.folderId}
                variant={selectedFolderId === folder.folderId ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedFolderId(folder.folderId)}
              >
                <FolderOpen size={14} className="mr-1" />
                {folder.name}
              </Button>
            ))}
            <Button variant="ghost" size="sm" onClick={() => setManageFolderOpen(true)}>
              <FolderPlus size={14} />
            </Button>
          </div>

          <Card className={`workout-card mb-10 ${showNewTemplateForm || editingId ? "" : "hidden"}`}>
            <CardHeader>
              <CardTitle id="template-header">{editingId ? "Edit Template" : "Create a New Template"}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label htmlFor="template-name" className="mb-1 block font-medium text-sm">
                      Template Name
                    </label>
                    <Input
                      id="template-name"
                      name="name"
                      value={workoutTemplate?.name || ""}
                      onChange={(e) => {
                        if (workoutTemplate) {
                          setWorkoutTemplate({
                            ...workoutTemplate,
                            name: e.target.value,
                          })
                        }
                      }}
                      placeholder="Push Day, Leg Day, etc."
                      className="workout-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="template-folder" className="mb-1 block font-medium text-sm">
                      Folder
                    </label>
                    <Select
                      value={workoutTemplate?.folderId || "__none__"}
                      onValueChange={(value) => {
                        if (workoutTemplate) {
                          setWorkoutTemplate({
                            ...workoutTemplate,
                            folderId: value === "__none__" ? null : value,
                          })
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="No folder" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No folder</SelectItem>
                        {folders.map((folder) => (
                          <SelectItem key={folder.folderId} value={folder.folderId}>
                            <div className="flex items-center gap-2">
                              <FolderOpen size={14} />
                              {folder.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="template-color" className="mb-1 block font-medium text-sm">
                      Color Tag
                    </label>
                    <Select value={color} onValueChange={setColor}>
                      <SelectTrigger className="w-full">
                        <SelectValue>
                          <div className="flex items-center">
                            <div
                              className={`mr-2 h-4 w-4 rounded-full`}
                              style={{
                                backgroundColor: templateColors[color || "red"],
                              }}
                            ></div>
                            <span className="capitalize">{color ?? "Select a color"}</span>
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(templateColors).map((color) => (
                          <SelectItem key={color} value={color}>
                            <div className="flex items-center">
                              <div
                                className={`mr-2 h-4 w-4 rounded-full`}
                                style={{
                                  backgroundColor: templateColors[color],
                                }}
                              ></div>
                              <span className="capitalize">{color}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label htmlFor="template-notes" className="mb-1 block font-medium text-sm">
                    Notes (Optional)
                  </label>
                  <Textarea
                    id="template-notes"
                    name="notes"
                    value={workoutTemplate?.notes || ""}
                    onChange={(e) => {
                      if (workoutTemplate) {
                        setWorkoutTemplate({
                          ...workoutTemplate,
                          notes: e.target.value,
                        })
                      }
                    }}
                    placeholder="Any notes about this template..."
                    rows={3}
                  />
                </div>

                <div>
                  <h3 className="mb-2 font-medium">Exercises</h3>
                  <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} onDragCancel={handleDragCancel}>
                    <SortableContext
                      items={exercises.map((x) => {
                        return { id: x.exerciseId as UniqueIdentifier }
                      })}
                      strategy={verticalListSortingStrategy}
                    >
                      {exercises.map((exercise) => (
                        <Draggable key={exercise.exerciseId} id={exercise.exerciseId as string}>
                          <ExerciseForm
                            exercise={exercise}
                            onUpdate={handleExerciseUpdate}
                            onDelete={handleExerciseDelete}
                            type="template"
                            status={draggingStatus}
                          />
                        </Draggable>
                      ))}
                    </SortableContext>
                  </DndContext>

                  <div className="mb-2 flex w-full items-center justify-between">
                    <Button type="button" variant="outline" onClick={handleAddExercise} className="mt-2">
                      <Plus size={16} className="mr-2" />
                      Add Exercise
                    </Button>

                    {draggingStatus === "dragging" && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setDraggingStatus("idle")
                        }}
                        className="mt-2"
                      >
                        Finish Reordering
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    {editingId ? "Cancel" : "Clear"}
                  </Button>
                  <Button type="button" disabled={isSavingTemplate} onClick={handleSubmit}>
                    {isSavingTemplate ? <div>Saving...</div> : editingId ? "Save Changes" : "Save Template"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {!isLoadingWorkoutTemplates ? (
            <div className="space-y-4">
              {selectedFolderId
                ? visibleTemplates.map(renderTemplateItem)
                : groupedTemplates && (
                    <>
                      {folders.map((folder) => {
                        const folderTemplates = groupedTemplates[folder.folderId]
                        if (!folderTemplates?.length) {
                          return null
                        }
                        return (
                          <div key={folder.folderId}>
                            <h4 className="mb-2 flex items-center gap-1 font-semibold text-muted-foreground text-sm">
                              <FolderOpen size={14} />
                              {folder.name}
                            </h4>
                            {folderTemplates.map(renderTemplateItem)}
                          </div>
                        )
                      })}
                      {groupedTemplates["__none__"]?.length > 0 && (
                        <div>
                          <h4 className="mb-2 flex items-center gap-1 font-semibold text-muted-foreground text-sm">
                            <Folder size={14} />
                            No Folder
                          </h4>
                          {groupedTemplates["__none__"].map(renderTemplateItem)}
                        </div>
                      )}
                    </>
                  )}
            </div>
          ) : (
            Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="mb-2 h-15 w-full border-border border-b py-3 last:border-0" />
            ))
          )}
          {!isLoadingWorkoutTemplates && workoutTemplates?.length === 0 && (
            <p className="text-muted-foreground">No templates saved. Create a template to get started!</p>
          )}
          {!isLoadingWorkoutTemplates && selectedFolderId && visibleTemplates.length === 0 && (
            <p className="text-muted-foreground">No templates in this folder.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={manageFolderOpen} onOpenChange={setManageFolderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Folders</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="New folder name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFolderName.trim()) {
                    createFolder(newFolderName.trim())
                  }
                }}
              />
              <Button onClick={() => createFolder(newFolderName.trim())} disabled={!newFolderName.trim()}>
                <Plus size={16} className="mr-1" />
                Add
              </Button>
            </div>
            <div className="space-y-2">
              {folders.map((folder) => (
                <div key={folder.folderId} className="flex items-center justify-between rounded-md border p-3">
                  {editingFolderId === folder.folderId ? (
                    <div className="flex flex-1 gap-2">
                      <Input
                        value={editingFolderName}
                        onChange={(e) => setEditingFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && editingFolderName.trim()) {
                            renameFolder({ folderId: folder.folderId, name: editingFolderName.trim() })
                          }
                          if (e.key === "Escape") {
                            setEditingFolderId(null)
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        onClick={() => renameFolder({ folderId: folder.folderId, name: editingFolderName.trim() })}
                        disabled={!editingFolderName.trim()}
                      >
                        <Check size={14} />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span className="font-medium">{folder.name}</span>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingFolderId(folder.folderId)
                            setEditingFolderName(folder.name)
                          }}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteFolder(folder.folderId)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {folders.length === 0 && (
                <p className="py-4 text-center text-muted-foreground text-sm">
                  No folders yet. Create your first folder above.
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importDialogOpen} onOpenChange={handleCloseImportDialog}>
        {!importResults ? (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import Templates</DialogTitle>
              <DialogDescription>
                {importFileName && importFileName.startsWith("http") ? "From Google Sheets" : `From: ${importFileName}`}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-60 space-y-2 overflow-y-auto">
              {importParsedData?.map((t) => {
                const isDuplicate = workoutTemplates.some((e) => e.name === t.name)
                return (
                  <div key={t.name} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                    {isDuplicate ? (
                      <span className="text-muted-foreground">⏭️ {t.name} — skipped (duplicate)</span>
                    ) : (
                      <span>
                        ✅ {t.name} ({t.jsonBlob.exercises.length} exercises)
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="text-muted-foreground text-sm">
              {(() => {
                const total = importParsedData?.length || 0
                const duplicates = importParsedData?.filter((t) =>
                  workoutTemplates.some((e) => e.name === t.name)
                ).length
                return `Will create: ${total - (duplicates ?? 0)}  ·  Skipped: ${duplicates ?? 0}`
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseImportDialog}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (importParsedData) {
                    importTemplates({
                      templates: importParsedData,
                      existingNames: workoutTemplates.map((t) => t.name),
                    })
                  }
                }}
                disabled={
                  isImporting || !importParsedData?.some((t) => !workoutTemplates.some((e) => e.name === t.name))
                }
              >
                {isImporting
                  ? "Importing..."
                  : `Import ${importParsedData?.filter((t) => !workoutTemplates.some((e) => e.name === t.name)).length || 0} Templates`}
              </Button>
            </DialogFooter>
          </DialogContent>
        ) : (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import Complete</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-4">
              <p>
                ✅ Created: {importResults.created} template
                {importResults.created !== 1 ? "s" : ""}
              </p>
              {importResults.skipped > 0 && (
                <p className="text-muted-foreground">⏭️ Skipped: {importResults.skipped} (duplicates)</p>
              )}
            </div>
            <DialogFooter>
              <Button onClick={handleCloseImportDialog}>Done</Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      <Dialog open={showGoogleSheetsDialog} onOpenChange={setShowGoogleSheetsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import from Google Sheets</DialogTitle>
            <DialogDescription>Paste the URL of a published Google Sheet containing template data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              value={googleSheetsUrl}
              onChange={(e) => setGoogleSheetsUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..."
            />
            <p className="text-muted-foreground text-xs">
              Make sure your sheet is shared publicly (File → Share → "Anyone with the link").
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowGoogleSheetsDialog(false)
                setGoogleSheetsUrl("")
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleImportFromGoogleSheets} disabled={isImportingSheets}>
              {isImportingSheets ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {!isLoadingArchivedWorkoutTemplates && archivedWorkoutTemplates.length > 0 && (
        <Card className="workout-card">
          <CardHeader>
            <CardTitle>Archived Templates</CardTitle>
          </CardHeader>
          <CardContent>
            {!isLoadingArchivedWorkoutTemplates ? (
              <div className="space-y-4">
                {archivedWorkoutTemplates.map((template: TWorkoutTemplate) => (
                  <div
                    key={template.workoutTemplateId}
                    className="flex items-center justify-between border-border border-b py-3 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      {template.color && (
                        <CircleDot
                          className={`h-4 w-4 text-blue-300 ${template.color
                            .replace("bg-", "text-")
                            .replace("-300", "-500")}`}
                          style={{ color: template.color }}
                        />
                      )}
                      <div>
                        <h3 className="font-medium">{template.name}</h3>
                        <div className="text-muted-foreground text-sm">
                          {template.jsonBlob.exercises.length} exercises
                        </div>
                        {template.lastDateCompleted && (
                          <div className="text-muted-foreground text-sm">
                            Last Completed:{" "}
                            {DateTime.fromISO(template.lastDateCompleted).toLocaleString(DateTime.DATE_HUGE)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Dialog
                        open={viewingId === template.workoutTemplateId}
                        onOpenChange={(open) => {
                          if (!open) {
                            setViewingId(null)
                          }
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setViewingId(template.workoutTemplateId)
                            }}
                          >
                            <Eye size={16} />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>{template.name}</DialogTitle>
                            <div className="mt-5 flex max-h-[75vh] flex-col gap-3 overflow-y-auto">
                              {template.jsonBlob.exercises.map((exercise: TExercise, exerciseIndex: number) => (
                                <div key={exercise.exerciseId ?? exerciseIndex} className="rounded-md border">
                                  <div className="flex items-center justify-between p-3">
                                    <div>
                                      <h4 className="font-medium">{exercise.name}</h4>
                                      <div className="text-muted-foreground text-sm">
                                        {exercise.sets.length} sets ·{"  "}
                                        {exercise.sets
                                          .reduce((total, set) => {
                                            return total + set.weight * set.reps
                                          }, 0)
                                          .toLocaleString()}{" "}
                                        lbs total
                                        {exercise.rpeMin || exercise.rpeMax
                                          ? ` · RPE ${exercise.rpeMin ?? "?"}–${exercise.rpeMax ?? "?"}`
                                          : ""}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="px-3 pb-3">
                                    <div className="grid grid-cols-3 gap-2 text-sm">
                                      {exercise.sets.map((set: TExerciseSet, index: number) => (
                                        <div
                                          key={set.setId ?? `${exerciseIndex}-${index}`}
                                          className="rounded-md bg-muted/30 p-2"
                                        >
                                          <div className="font-medium">Set {index + 1}</div>
                                          <div className="text-muted-foreground">
                                            {set.weight} lbs × {set.reps} reps
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {/*
                            <div className="mt-4 pt-4 border-t">
                              <h4 className="font-medium mb-2">Notes</h4>
                              <p className="text-muted-foreground whitespace-pre-wrap">
                                {template.notes}
                              </p>
                            </div>
                               * */}
                            </div>
                          </DialogHeader>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setViewingId(null)}>
                              Close
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="mb-2 h-15 w-full border-border border-b py-3 last:border-0" />
              ))
            )}
            {!isLoadingArchivedWorkoutTemplates && archivedWorkoutTemplates?.length === 0 && (
              <p className="text-muted-foreground">No archived templates.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
